import { createQuadBuffers, setupAttributes, createTexture, updateTexture } from '../utils/webgl.js';
import { calculateFitDimensions } from '../utils/file.js';
import { eventBus } from '../utils/EventEmitter.js';

/**
 * Manages the WebGL canvas and context
 */
export class CanvasManager {
    constructor(canvasId) {
        this.canvas = document.getElementById(canvasId);
        this.gl = this.canvas.getContext('webgl', {
            alpha: false,
            premultipliedAlpha: false,
            preserveDrawingBuffer: true
        });
        
        if (!this.gl) {
            throw new Error('WebGL not supported');
        }
        
        // Set clear color to white
        this.gl.clearColor(1.0, 1.0, 1.0, 1.0);
        
        // GIF state tracking
        this.currentGif = null;
        this.isAnimating = false;
        this.currentGifId = 0;
        this.animationFrame = null;
        
        // Camera state
        this.videoStream = null;
        this.videoElement = null;
        this.isCameraActive = false;
        this.currentFacingMode = 'user'; // Default to front camera
        
        // Previous source tracking
        this.previousSource = null;
        
        this.setupCanvas();
        this.setupEventListeners();
    }
    
    /**
     * Set up initial canvas state
     */
    setupCanvas() {
        // Create buffers for a full-screen quad
        this.buffers = createQuadBuffers(this.gl);
        
        // Create initial texture with white pixel
        const whitePixel = new Uint8Array([255, 255, 255, 255]);
        this.texture = createTexture(this.gl, new ImageData(new Uint8ClampedArray(whitePixel.buffer), 1, 1));
        
        // Default to 16:9 aspect ratio if no image is loaded
        this.imageAspectRatio = 16/9;
        
        // Handle initial sizing
        this.handleResize();
    }
    
    /**
     * Set up event listeners
     */
    setupEventListeners() {
        window.addEventListener('resize', () => this.handleResize());
        
        // Listen for new images
        eventBus.on('image:loaded', (image) => {
            // Stop any existing GIF animation
            this.currentGifId++;
            this.cleanupGif();
            this.isAnimating = false;
            
            // Handle static image
            this.updateTexture(image);
            this.updateImageDimensions(image);
            this.handleResize();
        });

        // Listen for GIF loading
        eventBus.on('image:loadGif', ({ url, shouldRevokeUrl }) => {
            this.handleGif(url, shouldRevokeUrl);
        });

        // Listen for URL loading
        eventBus.on('image:loadUrl', ({ url }) => {
            if (!url) return;
            
            // Handle both absolute and relative URLs
            let isValidUrl = true;
            try {
                // Try parsing as absolute URL first
                new URL(url);
            } catch {
                // If that fails, check if it's a valid relative URL (starts with /)
                if (!url.startsWith('/')) {
                    isValidUrl = false;
                }
            }
            
            if (!isValidUrl) {
                alert('Please enter a valid URL');
                return;
            }
            
            // Check if URL ends with .gif
            if (url.toLowerCase().endsWith('.gif')) {
                this.handleGif(url, false);
            } else {
                // Stop any existing GIF animation
                this.currentGifId++;
                
                // Handle static image
                const img = new Image();
                img.crossOrigin = 'anonymous'; // Enable CORS
                img.src = url;
                
                img.onload = () => {
                    this.updateTexture(img);
                    this.updateImageDimensions(img);
                    this.handleResize();
                };
                
                img.onerror = () => {
                    alert('Failed to load image. Make sure the URL is correct and the server allows cross-origin requests.');
                };
            }
        });
    }
    
    /**
     * Update the texture with a new image
     * @param {HTMLImageElement|HTMLCanvasElement} image - The source image
     */
    updateTexture(image) {
        updateTexture(this.gl, this.texture, image);
        // Store the last image for restoration after camera
        if (!(image instanceof HTMLVideoElement)) {
            this.lastImage = image;
        }
    }
    
    /**
     * Update canvas dimensions based on image
     * @param {HTMLImageElement|HTMLCanvasElement} image - The source image
     */
    updateImageDimensions(image) {
        this.imageWidth = image.naturalWidth || image.width;
        this.imageHeight = image.naturalHeight || image.height;
        this.imageAspectRatio = this.imageWidth / this.imageHeight;
        this.handleResize();
    }
    
    /**
     * Handle canvas resize
     */
    handleResize() {
        const container = this.canvas.parentElement;
        const rect = container.getBoundingClientRect();
        
        // Calculate dimensions that maintain aspect ratio
        const { width, height } = calculateFitDimensions(
            rect.width,
            rect.height,
            this.imageAspectRatio
        );
        
        // Update canvas size
        this.canvas.width = width;
        this.canvas.height = height;
        this.gl.viewport(0, 0, width, height);
        
        // Center the canvas in its container
        this.canvas.style.position = 'absolute';
        this.canvas.style.left = '50%';
        this.canvas.style.top = '50%';
        this.canvas.style.transform = 'translate(-50%, -50%)';
        
        // Emit resize event
        eventBus.emit('canvas:resized', { width, height });
    }
    
    /**
     * Draw a full-screen quad
     * @param {WebGLProgram} program - The shader program to use
     */
    drawQuad(program) {
        // Set up vertex attributes
        this.setupAttributes(program);
        
        // Bind texture to texture unit 0
        this.gl.activeTexture(this.gl.TEXTURE0);
        this.gl.bindTexture(this.gl.TEXTURE_2D, this.texture);
        
        // Draw the quad
        this.gl.drawArrays(this.gl.TRIANGLES, 0, 6);
    }
    
    /**
     * Set up vertex attributes for a shader program
     * @param {WebGLProgram} program - The shader program
     */
    setupAttributes(program) {
        const positionLocation = this.gl.getAttribLocation(program, 'a_position');
        const texCoordLocation = this.gl.getAttribLocation(program, 'a_texCoord');

        // Set up position attribute
        this.gl.bindBuffer(this.gl.ARRAY_BUFFER, this.buffers.position);
        this.gl.enableVertexAttribArray(positionLocation);
        this.gl.vertexAttribPointer(positionLocation, 2, this.gl.FLOAT, false, 0, 0);

        // Set up texcoord attribute
        this.gl.bindBuffer(this.gl.ARRAY_BUFFER, this.buffers.texcoord);
        this.gl.enableVertexAttribArray(texCoordLocation);
        this.gl.vertexAttribPointer(texCoordLocation, 2, this.gl.FLOAT, false, 0, 0);
    }
    
    /**
     * Clear the canvas
     */
    clear() {
        this.gl.clear(this.gl.COLOR_BUFFER_BIT);
    }
    
    /**
     * Get the WebGL context
     * @returns {WebGLRenderingContext}
     */
    getContext() {
        return this.gl;
    }
    
    /**
     * Get the canvas element
     * @returns {HTMLCanvasElement}
     */
    getCanvas() {
        return this.canvas;
    }
    
    /**
     * Get current canvas dimensions
     * @returns {Object} Object containing width and height
     */
    getDimensions() {
        return {
            width: this.canvas.width,
            height: this.canvas.height
        };
    }

    /**
     * Handle GIF input
     * @param {string} gifUrl - URL of the GIF
     * @param {boolean} shouldRevokeUrl - Whether to revoke the object URL after loading
     */
    async handleGif(gifUrl, shouldRevokeUrl = false) {
        // Stop any existing GIF animation
        this.currentGifId++;
        const thisGifId = this.currentGifId;
        
        // Reset GIF state
        this.cleanupGif();
        
        // Initialize GIF info before loading frames
        this.currentGif = {
            frames: [],
            width: 0,
            height: 0,
            totalDuration: 0,
            frameCount: 0,
            url: gifUrl  // Store the URL for restoration after camera
        };
        
        // Create working canvases
        const baseCanvas = document.createElement('canvas');
        const currentCanvas = document.createElement('canvas');
        const baseCtx = baseCanvas.getContext('2d');
        const currentCtx = currentCanvas.getContext('2d');
        
        // Enable smooth scaling
        baseCtx.imageSmoothingEnabled = true;
        currentCtx.imageSmoothingEnabled = true;
        
        try {
            // Use gifler to handle the GIF
            const animation = gifler(gifUrl);
            
            animation.frames(currentCanvas, (ctx, frame) => {
                // Check if this animation is still current
                if (this.currentGifId !== thisGifId) return;
                
                // First frame - set up canvas sizes and store dimensions
                if (!baseCanvas.width) {
                    this.imageWidth = frame.width;
                    this.imageHeight = frame.height;
                    this.imageAspectRatio = frame.width / frame.height;
                    
                    // Set canvas sizes to match the container while maintaining aspect ratio
                    const container = this.canvas.parentElement;
                    const rect = container.getBoundingClientRect();
                    const { width, height } = calculateFitDimensions(rect.width, rect.height, this.imageAspectRatio);
                    
                    // Set both working canvases to the scaled dimensions
                    baseCanvas.width = width;
                    baseCanvas.height = height;
                    currentCanvas.width = width;
                    currentCanvas.height = height;
                    
                    // Store initial frame as base, properly scaled
                    baseCtx.drawImage(frame.buffer, 0, 0, frame.width, frame.height, 0, 0, width, height);
                    
                    // Update GIF info with dimensions
                    this.currentGif.width = frame.width;
                    this.currentGif.height = frame.height;
                    this.currentGif.canvas = currentCanvas;
                    this.currentGif.ctx = currentCtx;
                    this.currentGif.baseCanvas = baseCanvas;
                    this.currentGif.baseCtx = baseCtx;
                    
                    // Emit loaded event with initial dimensions
                    eventBus.emit('gif:loaded', {
                        width: frame.width,
                        height: frame.height,
                        frameCount: 0,
                        duration: 0
                    });
                }
                
                // Track frame info
                if (frame.delay > 0) {
                    this.currentGif.frames.push({
                        delay: frame.delay || 100, // Default to 100ms if delay is 0
                        disposalType: frame.disposalType
                    });
                    this.currentGif.totalDuration += frame.delay || 100;
                    this.currentGif.frameCount++;
                    
                    // Update loaded event with new frame count
                    eventBus.emit('gif:loaded', {
                        width: this.currentGif.width,
                        height: this.currentGif.height,
                        frameCount: this.currentGif.frameCount,
                        duration: this.currentGif.totalDuration
                    });
                }
                
                // Handle frame disposal
                if (frame.disposalType === 2) {
                    // Clear and restore to base frame
                    currentCtx.clearRect(0, 0, currentCanvas.width, currentCanvas.height);
                    currentCtx.drawImage(baseCanvas, 0, 0);
                }
                
                // Draw the current frame scaled to fit
                currentCtx.drawImage(
                    frame.buffer,
                    0, 0, frame.width, frame.height,
                    0, 0, currentCanvas.width, currentCanvas.height
                );
                
                // Update WebGL texture
                this.updateTexture(currentCanvas);
            });
            
            // Clean up URL if needed
            if (shouldRevokeUrl) {
                URL.revokeObjectURL(gifUrl);
            }
            
            this.isAnimating = true;
            
        } catch (error) {
            console.error('Failed to load GIF:', error);
            eventBus.emit('gif:error', error);
            this.cleanupGif();
        }
    }

    /**
     * Check if a GIF is currently loaded
     * @returns {boolean} True if a GIF is loaded, false otherwise
     */
    isGif() {
        return this.currentGif !== null;
    }

    /**
     * Get current GIF canvas if available
     * @returns {HTMLCanvasElement|null}
     */
    getGifCanvas() {
        return this.currentGif?.canvas || null;
    }

    /**
     * Clean up GIF resources
     */
    cleanupGif() {
        // Cancel any pending animation frame
        if (this.animationFrame) {
            cancelAnimationFrame(this.animationFrame);
            this.animationFrame = null;
        }
        
        // Clean up canvases if they exist
        if (this.currentGif?.canvas) {
            this.currentGif.canvas.width = 0;
            this.currentGif.canvas.height = 0;
        }
        
        if (this.currentGif?.baseCanvas) {
            this.currentGif.baseCanvas.width = 0;
            this.currentGif.baseCanvas.height = 0;
        }
        
        // Reset GIF state
        this.currentGif = null;
        this.isAnimating = false;
    }

    /**
     * Start camera capture
     */
    async startCamera() {
        try {
            // Check if mediaDevices API is supported
            if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
                // Try fallback for older browsers
                const getUserMedia = navigator.getUserMedia || 
                                   navigator.webkitGetUserMedia ||
                                   navigator.mozGetUserMedia ||
                                   navigator.msGetUserMedia;
                
                if (!getUserMedia) {
                    throw new Error('Camera access is not supported in this browser. Please try using a modern browser with HTTPS.');
                }
            }

            // Store current state as previous source if we have a GIF or image loaded
            if (this.currentGif) {
                this.previousSource = {
                    type: 'gif',
                    url: this.currentGif.url
                };
            } else if (this.lastImage) {
                this.previousSource = {
                    type: 'image',
                    image: this.lastImage
                };
            }

            // Stop any existing GIF animation
            this.currentGifId++;
            this.cleanupGif();
            this.isAnimating = false;

            // Request camera access with error handling
            let stream;
            try {
                stream = await navigator.mediaDevices.getUserMedia({ 
                    video: { 
                        facingMode: this.currentFacingMode,
                        width: { ideal: 1920 },
                        height: { ideal: 1080 }
                    } 
                });
            } catch (err) {
                if (err.name === 'NotAllowedError') {
                    throw new Error('Camera access was denied. Please grant permission to use your camera.');
                } else if (err.name === 'NotFoundError') {
                    throw new Error('No camera found. Please ensure your device has a working camera.');
                } else if (err.name === 'NotReadableError') {
                    throw new Error('Camera is in use by another application. Please close other apps using the camera.');
                } else {
                    throw new Error(`Camera error: ${err.message}`);
                }
            }
            
            // Create video element if it doesn't exist
            if (!this.videoElement) {
                this.videoElement = document.createElement('video');
                this.videoElement.playsInline = true;
                this.videoElement.autoplay = true;
            }
            
            // Set up video stream
            this.videoElement.srcObject = stream;
            this.videoStream = stream;
            this.isCameraActive = true;
            
            // Wait for video to be ready
            await new Promise((resolve, reject) => {
                this.videoElement.onloadedmetadata = () => {
                    this.videoElement.play().then(resolve).catch(reject);
                };
                this.videoElement.onerror = () => reject(new Error('Failed to start video playback'));
            });
            
            // Update dimensions
            this.imageWidth = this.videoElement.videoWidth;
            this.imageHeight = this.videoElement.videoHeight;
            
            // Start camera render loop
            this.renderCamera();
            
            // Show camera switch button if on mobile
            if ('ontouchstart' in window) {
                const switchButton = document.getElementById('cameraSwitchButton');
                if (switchButton) {
                    switchButton.style.display = 'flex';
                }
            }
            
            // Emit camera started event
            eventBus.emit('camera:started', {
                width: this.imageWidth,
                height: this.imageHeight,
                facingMode: this.currentFacingMode
            });
            
        } catch (error) {
            console.error('Failed to start camera:', error);
            eventBus.emit('camera:error', error);
            throw error;
        }
    }
    
    /**
     * Switch between front and back cameras
     */
    async switchCamera() {
        if (!this.isCameraActive) return;
        
        try {
            // Stop current stream
            if (this.videoStream) {
                this.videoStream.getTracks().forEach(track => track.stop());
            }
            
            // Toggle facing mode
            this.currentFacingMode = this.currentFacingMode === 'user' ? 'environment' : 'user';
            
            // Restart camera with new facing mode
            await this.startCamera();
            
            // Emit camera switched event
            eventBus.emit('camera:switched', {
                facingMode: this.currentFacingMode
            });
            
        } catch (error) {
            console.error('Failed to switch camera:', error);
            eventBus.emit('camera:error', error);
            throw error;
        }
    }
    
    /**
     * Stop camera capture
     */
    stopCamera() {
        if (this.videoStream) {
            // Stop all tracks
            this.videoStream.getTracks().forEach(track => track.stop());
            this.videoStream = null;
            
            // Clean up video element
            if (this.videoElement) {
                this.videoElement.srcObject = null;
                this.videoElement.pause();
                this.videoElement = null;
            }
            
            this.isCameraActive = false;
            
            // Hide camera switch button
            const switchButton = document.getElementById('cameraSwitchButton');
            if (switchButton) {
                switchButton.style.display = 'none';
            }
            
            // Restore previous source if available
            if (this.previousSource) {
                if (this.previousSource.type === 'gif') {
                    this.handleGif(this.previousSource.url, false);
                } else if (this.previousSource.type === 'image') {
                    this.updateTexture(this.previousSource.image);
                    this.updateImageDimensions(this.previousSource.image);
                }
                this.previousSource = null;
            } else {
                // If no previous source, just clear the canvas
                this.gl.clear(this.gl.COLOR_BUFFER_BIT);
            }
            
            // Emit camera stopped event
            eventBus.emit('camera:stopped');
        }
    }
    
    /**
     * Render camera frame to canvas
     */
    renderCamera = () => {
        if (!this.isCameraActive) return;
        
        // Update texture with current video frame
        if (this.videoElement.readyState === this.videoElement.HAVE_ENOUGH_DATA) {
            this.updateTexture(this.videoElement);
        }
        
        // Request next frame
        requestAnimationFrame(this.renderCamera);
    }
} 